import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Mail, ShoppingCart, Loader2, Lock } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useCheckout() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [phase, setPhase] = useState("email"); // email | processing | redirecting
  const [productId, setProductId] = useState(null);

  const startCheckout = (id) => {
    setProductId(id);
    setEmail("");
    setPhase("email");
    setOpen(true);
  };

  const submit = async () => {
    if (phase !== "email") return;
    const trimmed = email.trim();
    if (!trimmed || !trimmed.includes("@") || !trimmed.includes(".")) {
      toast.error("Please enter a valid email address");
      return;
    }
    setPhase("processing");
    try {
      const { data } = await axios.post(`${API}/checkout/session`, {
        product_id: productId,
        origin_url: window.location.origin,
        customer_email: trimmed,
      });
      if (data.url) {
        setPhase("redirecting");
        window.location.href = data.url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed. Please try again.");
      setPhase("email");
    }
  };

  const modal = (
    <Dialog open={open} onOpenChange={(v) => { if (phase === "email") setOpen(v); }}>
      <DialogContent className="bg-[#111111] border-white/10 max-w-[360px] p-6 sm:p-8">
        {phase === "email" && (
          <>
            <DialogHeader className="mb-1">
              <DialogTitle className="font-heading text-xl text-center">CHECKOUT</DialogTitle>
              <DialogDescription className="text-white/40 text-xs text-center leading-relaxed">
                Enter your email to receive your receipt and download link.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-white/40 mb-1.5 block font-semibold">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20 pointer-events-none" />
                  <input
                    data-testid="checkout-email-input"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && submit()}
                    placeholder="you@example.com"
                    autoFocus
                    className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#D4AF37] outline-none pl-10 pr-4 py-3.5 text-sm text-white rounded placeholder:text-white/20 transition-colors"
                  />
                </div>
              </div>
              <Button
                data-testid="checkout-submit-btn"
                onClick={submit}
                className="w-full bg-[#D4AF37] text-black hover:bg-white font-semibold py-3.5 uppercase tracking-wider text-xs h-auto touch-manipulation"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                Continue to Payment
              </Button>
              <div className="flex items-center justify-center gap-1.5 text-[10px] text-white/25">
                <Lock className="w-3 h-3" />
                Secure checkout powered by Stripe
              </div>
            </div>
          </>
        )}

        {phase === "processing" && (
          <div className="py-8 text-center">
            <Loader2 className="w-10 h-10 text-[#D4AF37] mx-auto animate-spin mb-4" />
            <p className="font-heading text-lg">PREPARING CHECKOUT</p>
            <p className="text-white/30 text-xs mt-2">Setting up your secure payment...</p>
          </div>
        )}

        {phase === "redirecting" && (
          <div className="py-8 text-center">
            <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <p className="font-heading text-lg">REDIRECTING TO STRIPE</p>
            <p className="text-white/30 text-xs mt-2">You're being taken to the secure payment page...</p>
            <div className="mt-4 h-1 bg-white/5 rounded-full overflow-hidden">
              <div className="h-full bg-[#D4AF37] rounded-full animate-pulse w-3/4" />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );

  return { startCheckout, modal };
}
